import { Button, Input } from '@orbind/ui'
import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { api } from '../../lib/api.ts'
import { TAMANHO_MAXIMO_DO_FUNDO, urlDoFundo, type FundoDaBiblioteca } from './fundo-de-quadro.ts'
import './fundo-de-quadro.css'

/**
 * O que a pessoa escolheu como imagem de fundo
 * (SPEC-20261027-1840-biblioteca-de-fundos-e-imagem-do-quadro).
 *
 * - `url`: da biblioteca ou colada — vai direto no quadro;
 * - `arquivo`: do computador — sobe para o armazenamento quando o quadro
 *   existir (o quadro novo ainda não tem onde guardar).
 */
export type ImagemEscolhida = { tipo: 'url'; url: string } | { tipo: 'arquivo'; arquivo: File; previa: string }

/**
 * Painel do botão "Imagem" das cores de fundo: biblioteca global, envio do
 * computador ou URL. Fica DENTRO da tela (preferência do Cristian: nada de
 * janela sobre janela), logo abaixo das cores.
 */
export function EscolherImagemDeFundo({
  selecionada,
  onEscolher,
  onErro,
}: {
  /** Endereço da imagem escolhida agora (para marcar na biblioteca). */
  selecionada: string | null
  onEscolher: (escolha: ImagemEscolhida) => void
  onErro: (mensagem: string) => void
}) {
  const arquivo = useRef<HTMLInputElement>(null)
  const [url, setUrl] = useState('')
  const biblioteca = useQuery({
    queryKey: ['kanbans', 'background-library'],
    queryFn: async () => (await api.kanbans['background-library'].$get()).json(),
    staleTime: 5 * 60 * 1000,
  })
  // tudo do R2: as que vêm com o Orbind e as enviadas pelo super admin
  const imagens: FundoDaBiblioteca[] = biblioteca.data?.imagens ?? []

  const usarUrl = () => {
    const limpa = url.trim()
    if (!limpa) return
    if (!/^https?:\/\//i.test(limpa)) {
      onErro('Use um endereço que comece com http:// ou https://.')
      return
    }
    onEscolher({ tipo: 'url', url: limpa })
    setUrl('')
  }

  return (
    <div className="kbimg" role="group" aria-label="Imagem de fundo">
      <div className="kbimg__secao">
        <span className="kbimg__titulo">Biblioteca</span>
        <div className="kbimg__grade">
          {imagens.map((img) => {
            const marcada = selecionada === img.url
            return (
              <button
                key={img.id}
                type="button"
                className={`kbimg__miniatura${marcada ? ' kbimg__miniatura--on' : ''}`}
                style={{ backgroundImage: `url("${urlDoFundo(img.url)}")` }}
                aria-label={`Fundo ${img.name}`}
                aria-pressed={marcada}
                title={img.name}
                onClick={() => onEscolher({ tipo: 'url', url: img.url })}
              >
                {marcada ? <i className="fas fa-check" aria-hidden /> : null}
              </button>
            )
          })}
        </div>
        {biblioteca.isPending ? (
          <span className="kbimg__aviso kbimg__aviso--neutro">Carregando a biblioteca…</span>
        ) : biblioteca.isError ? (
          <span className="kbimg__aviso">Não deu para carregar a biblioteca. Envie do computador ou use uma URL.</span>
        ) : imagens.length === 0 ? (
          <span className="kbimg__aviso kbimg__aviso--neutro">A biblioteca ainda está vazia.</span>
        ) : null}
      </div>

      <div className="kbimg__linha">
        <Button variant="secondary" icon="fas fa-upload" onClick={() => arquivo.current?.click()}>
          Enviar do computador
        </Button>
        <input
          ref={arquivo}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const escolhido = e.target.files?.[0]
            e.target.value = ''
            if (!escolhido) return
            if (!escolhido.type.startsWith('image/')) {
              onErro('Escolha um arquivo de imagem.')
              return
            }
            if (escolhido.size > TAMANHO_MAXIMO_DO_FUNDO) {
              onErro('Imagem grande demais: o limite é 8 MB.')
              return
            }
            onEscolher({ tipo: 'arquivo', arquivo: escolhido, previa: URL.createObjectURL(escolhido) })
          }}
        />
      </div>

      <div className="kbimg__linha">
        <Input
          value={url}
          placeholder="Ou cole a URL de uma imagem"
          aria-label="URL da imagem de fundo"
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              usarUrl()
            }
          }}
        />
        <Button variant="secondary" disabled={url.trim() === ''} onClick={usarUrl}>
          Usar
        </Button>
      </div>
    </div>
  )
}
